import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Search, RefreshCcw, KeyRound, UserX, Trash2, LogIn, ShieldAlert } from "lucide-react";

export default function SuperAdminUsers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role !== "all") params.role = role;
      const { data } = await api.get("/superadmin/users", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, role]);

  useEffect(() => { load(); }, [load]);

  async function resetPassword(user) {
    const pw = window.prompt(`Nuova password per ${user.email} (min 6 caratteri):`);
    if (!pw) return;
    try {
      await api.post(`/superadmin/users/${user.user_id}/reset-password`, { new_password: pw });
      toast.success(`Password reimpostata per ${user.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function toggleBan(user) {
    const verb = user.banned ? "sbannare" : "bannare";
    if (!window.confirm(`Confermi di voler ${verb} ${user.email}?`)) return;
    try {
      const { data } = await api.post(`/superadmin/users/${user.user_id}/ban`);
      toast.success(data.banned ? "Utente bannato" : "Utente sbannato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function deleteUser(user) {
    if (!window.confirm(`Eliminare definitivamente l'account ${user.email}?`)) return;
    try {
      await api.delete(`/superadmin/users/${user.user_id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function impersonate(user) {
    if (!window.confirm(`Vuoi accedere come ${user.email}? La tua sessione Super Admin verrà sostituita.`)) return;
    try {
      await api.post(`/superadmin/impersonate/${user.user_id}`);
      toast.success(`Ora impersoni ${user.email}. Reindirizzamento…`);
      setTimeout(() => { window.location.href = "/dashboard"; }, 800);
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="p-6 sm:p-8 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight" data-testid="sa-users-title">Gestione Utenti</h1>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-slate-700 hover:border-rose-500 hover:bg-rose-500/10 text-sm text-slate-300"
        >
          <RefreshCcw size={14}/> Ricarica
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-center bg-slate-900/60 border border-slate-800 rounded-xl p-3">
        <div className="flex-1 min-w-[240px] relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
          <input
            data-testid="sa-users-search"
            className="w-full bg-slate-950 border border-slate-700 rounded-md pl-9 pr-4 py-2 text-sm focus:border-rose-500 outline-none"
            placeholder="Cerca per nome o email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <select
          data-testid="sa-users-role-filter"
          className="bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <option value="all">Tutti i ruoli</option>
          <option value="user">User</option>
          <option value="admin">Admin</option>
          <option value="superadmin">SuperAdmin</option>
        </select>
        <div className="text-xs text-slate-500 font-mono">{rows.length} utenti</div>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-950/70 text-[10px] uppercase tracking-widest text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left">Utente</th>
                <th className="px-4 py-3 text-center">Ruolo</th>
                <th className="px-4 py-3 text-center">Leghe (admin)</th>
                <th className="px-4 py-3 text-center">Team (manager)</th>
                <th className="px-4 py-3 text-center">Login</th>
                <th className="px-4 py-3 text-left">Ultimo accesso</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">Caricamento…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-slate-500">Nessun utente trovato</td></tr>
              ) : rows.map((u) => (
                <tr key={u.user_id} className={`hover:bg-slate-800/30 ${u.banned ? "opacity-60" : ""}`} data-testid={`sa-user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-bold text-slate-100 flex items-center gap-2">
                      {u.name || "—"}
                      {u.banned && (
                        <span className="text-[10px] uppercase font-bold text-rose-400 border border-rose-500/40 bg-rose-500/10 px-1.5 py-0.5 rounded">
                          BAN
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500">{u.email}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded border ${
                      u.role === "superadmin"
                        ? "border-rose-500/40 bg-rose-500/15 text-rose-300"
                        : u.role === "admin"
                        ? "border-amber-500/40 bg-amber-500/15 text-amber-300"
                        : "border-slate-700 bg-slate-800/40 text-slate-300"
                    }`}>
                      {u.role || "user"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-mono">{u.admin_leagues}</td>
                  <td className="px-4 py-3 text-center font-mono">{u.member_teams}</td>
                  <td className="px-4 py-3 text-center font-mono">{u.login_count || 0}</td>
                  <td className="px-4 py-3 text-xs text-slate-400 font-mono">
                    {u.last_login_at ? u.last_login_at.slice(0, 16).replace("T", " ") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button
                      onClick={() => resetPassword(u)}
                      title="Reset password"
                      data-testid={`sa-reset-pw-${u.user_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-sky-600/40 bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 text-xs"
                    >
                      <KeyRound size={12}/>
                    </button>
                    {u.role !== "superadmin" && (
                      <button
                        onClick={() => impersonate(u)}
                        title="Impersona (Login as)"
                        data-testid={`sa-impersonate-${u.user_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-violet-600/40 bg-violet-500/10 hover:bg-violet-500/20 text-violet-300 text-xs"
                      >
                        <LogIn size={12}/>
                      </button>
                    )}
                    {u.role !== "superadmin" && (
                      <button
                        onClick={() => toggleBan(u)}
                        title={u.banned ? "Sblocca utente" : "Banna utente"}
                        data-testid={`sa-ban-${u.user_id}`}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs ${
                          u.banned
                            ? "border-emerald-600/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300"
                            : "border-amber-600/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300"
                        }`}
                      >
                        {u.banned ? <ShieldAlert size={12}/> : <UserX size={12}/>}
                      </button>
                    )}
                    {u.role !== "superadmin" && (
                      <button
                        onClick={() => deleteUser(u)}
                        title="Elimina utente"
                        data-testid={`sa-delete-user-${u.user_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-600/40 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 text-xs"
                      >
                        <Trash2 size={12}/>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
